import { ReactElement } from "react";
import { type ColorType, blue, pink, purple } from "../../../theme/colors";
import { Divider } from "@kunduz/ui";
import { PlanV2 } from "./planV2";
import { type IContent } from "@kunduz/data-access-cms-api";
import dynamic from "next/dynamic";

const WistiaPlayer = dynamic(
  () => import("@wistia/wistia-player-react").then((mod) => mod.WistiaPlayer),
  {
    ssr: false,
    loading() {
      return (
        <div style={{ width: "100%", aspectRatio: "16/9" }}>Loading...</div>
      );
    },
  },
);

const colors = {
  blue: blue,
  pink: pink,
  purple: purple,
};

function getColorTheme(_theme: "blue" | "pink" | "purple"): ColorType {
  return colors[_theme] || colors.blue;
}

export const PlanV2Card = (props: Props): ReactElement => {
  return (
    <PlanV2.Base bgColor={getColorTheme(props.colorTheme)[50]}>
      {props.diagonalBadge && (
        <PlanV2.DiagonalBadge bgColor={getColorTheme(props.colorTheme)[600]}>
          {props.diagonalBadge}
        </PlanV2.DiagonalBadge>
      )}
      <PlanV2.Content>
        <PlanV2.PackegeInfo
          title={props.packageName}
          badgeText={props.featureBadge}
          rating={props.rating}
          reviewText={props.reviewText}
          theme={props.colorTheme}
        />

        <Divider
          top={0}
          bottom={0}
          color={getColorTheme(props.colorTheme)[100]}
        />

        {props.videoId && (
          <>
            <div>
              <WistiaPlayer
                mediaId={props.videoId}
                aspect={16 / 9}
                playerColor={getColorTheme(props.colorTheme)[600]}
                controlsVisibleOnLoad={false}
                roundedPlayer={8}
                volumeControl={false}
                fullscreenControl={false}
                wistiaPopover
                onPlay={() =>
                  props.onClick(props.packageName, props.packagePriceId as any)
                }
              />
            </div>
            <Divider
              top={0}
              bottom={0}
              color={getColorTheme(props.colorTheme)[100]}
            />
          </>
        )}
        <PlanV2.PricingInfo
          fullPrice={props.fullPrice}
          highlightText={props.highlightText}
          monthlyPrice={props.monthlyPrice}
          period={props.period}
          discountBadge={props.discountBadge}
          theme={getColorTheme(props.colorTheme)}
        />

        <Divider
          top={0}
          bottom={0}
          color={getColorTheme(props.colorTheme)[100]}
        />

        <PlanV2.CtaWrapper>
          {props.secondaryCtaLabel && (
            <PlanV2.CtaButton
              href={props.secondaryCtaUrl}
              label={props.secondaryCtaLabel}
              type={props.secondaryCtaType}
              color={props.colorTheme}
              onClick={props.onSecondaryCtaClicked}
            />
          )}
          {props.primaryCtaLabel && (
            <PlanV2.CtaButton
              href={props.primaryCtaUrl}
              label={props.primaryCtaLabel}
              type={props.primaryCtaType}
              color={props.colorTheme}
              onClick={props.onPrimaryCtaClicked}
            />
          )}
        </PlanV2.CtaWrapper>

        {props.summaryOptions && (
          <>
            <Divider
              top={0}
              bottom={0}
              color={getColorTheme(props.colorTheme)[100]}
            />

            <div style={{ display: "flex", gap: 8 }}>
              {props.summaryOptions?.feature_group && (
                <PlanV2.SummaryContainer>
                  <PlanV2.SummaryTitle
                    name={props.summaryOptions.feature_group.name}
                  />
                  {props.summaryOptions.feature_group.features.map((f, i) => (
                    <PlanV2.SummaryItem
                      iconColor={getColorTheme(props.colorTheme)[600]}
                      key={`f_${i}`}
                      text={f.text}
                      inactive={f.is_strikethrough}
                    />
                  ))}
                </PlanV2.SummaryContainer>
              )}
              {props.summaryOptions?.benefit_group ||
              props.summaryOptions?.content_group ? (
                <PlanV2.SummaryContainer>
                  {props.summaryOptions.content_group && (
                    <>
                      <PlanV2.SummaryTitle
                        name={props.summaryOptions.content_group.name}
                      />
                      {props.summaryOptions.content_group.contents.map(
                        (c, i) => (
                          <PlanV2.SummaryItem
                            iconColor={getColorTheme(props.colorTheme)[600]}
                            key={`c_${i}`}
                            text={c.text}
                            inactive={c.is_strikethrough}
                          />
                        ),
                      )}
                    </>
                  )}
                  {props.summaryOptions.benefit_group && (
                    <>
                      <PlanV2.SummaryTitle
                        name={props.summaryOptions.benefit_group.name}
                      />
                      {props.summaryOptions.benefit_group.benefits.map(
                        (b, i) => (
                          <PlanV2.SummaryItem
                            iconColor={getColorTheme(props.colorTheme)[600]}
                            key={`b_${i}`}
                            text={b.text}
                            inactive={b.is_strikethrough}
                          />
                        ),
                      )}
                    </>
                  )}
                </PlanV2.SummaryContainer>
              ) : (
                <></>
              )}
            </div>
          </>
        )}
      </PlanV2.Content>
    </PlanV2.Base>
  );
};

export const PlanV2CardMini = (props: Props): ReactElement => {
  return (
    <PlanV2.Base bgColor={getColorTheme(props.colorTheme)[50]}>
      {props.diagonalBadge && (
        <PlanV2.DiagonalBadge bgColor={getColorTheme(props.colorTheme)[600]}>
          {props.diagonalBadge}
        </PlanV2.DiagonalBadge>
      )}
      <PlanV2.Content>
        <PlanV2.PackegeInfo
          title={props.packageName}
          badgeText={props.featureBadge}
          rating={props.rating}
          reviewText={props.reviewText}
          theme={props.colorTheme}
        />

        <Divider
          top={0}
          bottom={0}
          color={getColorTheme(props.colorTheme)[100]}
        />

        {props.summaryOptions && (
          <>
            <Divider
              top={0}
              bottom={0}
              color={getColorTheme(props.colorTheme)[100]}
            />

            <div style={{ display: "flex", gap: 8 }}>
              {props.summaryOptions?.feature_group && (
                <PlanV2.SummaryContainer>
                  <PlanV2.SummaryTitle
                    name={props.summaryOptions.feature_group.name}
                  />
                  {props.summaryOptions.feature_group.features.map((f, i) => (
                    <PlanV2.SummaryItem
                      iconColor={getColorTheme(props.colorTheme)[600]}
                      key={`f_${i}`}
                      text={f.text}
                    />
                  ))}
                </PlanV2.SummaryContainer>
              )}
              {props.summaryOptions?.benefit_group ||
              props.summaryOptions?.content_group ? (
                <PlanV2.SummaryContainer>
                  {props.summaryOptions.benefit_group && (
                    <>
                      <PlanV2.SummaryTitle
                        name={props.summaryOptions.benefit_group.name}
                      />
                      {props.summaryOptions.benefit_group.benefits.map(
                        (b, i) => (
                          <PlanV2.SummaryItem
                            iconColor={getColorTheme(props.colorTheme)[600]}
                            key={`b_${i}`}
                            text={b.text}
                          />
                        ),
                      )}
                    </>
                  )}
                  {props.summaryOptions.content_group && (
                    <>
                      <PlanV2.SummaryTitle
                        name={props.summaryOptions.content_group.name}
                      />
                      {props.summaryOptions.content_group.contents.map(
                        (c, i) => (
                          <PlanV2.SummaryItem
                            iconColor={getColorTheme(props.colorTheme)[600]}
                            key={`c_${i}`}
                            text={c.text}
                          />
                        ),
                      )}
                    </>
                  )}
                </PlanV2.SummaryContainer>
              ) : (
                <></>
              )}
            </div>
          </>
        )}

        <Divider
          top={0}
          bottom={0}
          color={getColorTheme(props.colorTheme)[100]}
        />

        <PlanV2.CtaWrapper>
          {props.secondaryCtaUrl && (
            <PlanV2.CtaButton
              href={props.secondaryCtaUrl}
              label={props.secondaryCtaLabel}
              type={props.secondaryCtaType}
              color={props.colorTheme}
              onClick={props.onSecondaryCtaClicked}
            />
          )}
        </PlanV2.CtaWrapper>
      </PlanV2.Content>
    </PlanV2.Base>
  );
};

interface Props {
  colorTheme: "blue" | "pink" | "purple";
  featureBadge: string;
  rating: string;
  reviewText: string;
  diagonalBadge: string;
  packageName: string;
  fullPrice: string;
  discountBadge: string;
  monthlyPrice: string;
  period: string;
  highlightText: string;
  secondaryCtaLabel: string;
  secondaryCtaUrl: string;
  secondaryCtaType: string;
  onSecondaryCtaClicked?: (e: any) => void;
  primaryCtaLabel: string;
  primaryCtaUrl: string;
  primaryCtaType: string;
  onPrimaryCtaClicked?: (e: any) => void;
  summaryOptions?: IContent.PricingComponentData["prices"][0]["summary_option"];
  videoId?: string;
  onClick: (name: string, id: string) => void;
  packageId: number;
  packagePriceId: number;
}
